/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import { BOOKING_STATUS, ROUTES } from "./constants/constants";
import Locations from "./pages/Locations";
import Services from "./pages/Services";
import Classes from "./pages/Classes";
import Home from "./pages/Home";
import Providers from "./pages/Providers";
import DateTime from "./pages/DateTime";
import Details from "./pages/Details";
import AdditionalFields from "./pages/AdditionalFields";
import Checkout from "./pages/Checkout";
import ClientProfile from "./pages/client/ClientProfile";
import ClientProfileEdit from "./pages/client/ClientProfileEdit";
import ClientPasswordChange from "./pages/client/ClientPasswordChange";
import ClientBookings from "./pages/client/ClientBookings";
import ClientPackages from "./pages/client/ClientPackages";
import ClientRegister from "./pages/client/ClientRegister";
import BookingStatus from "./pages/BookingStatus";
import PersonalDataReport from "./pages/PersonalDataReport";
import ClientInvoices from "./pages/client/ClientInvoices";
import ClientPayments from "./pages/client/ClientPayments";
import ClientPaymentMethods from "./pages/client/ClientPaymentMethods";
import ClientRecurringProfiles from "./pages/client/ClientRecurringProfiles";
import InvoicePay from "./pages/InvoicePay";
import InvoiceDetails from "./pages/InvoiceDetails";
import CancelBooking from "./pages/CancelBooking";
import RescheduleBooking from "./pages/RescheduleBooking";
import Addons from "./pages/Addons";
import Products from "./pages/Products";
import ProductsForSale from "./pages/ProductsForSale";
import Packages from "./pages/Packages";
import CartItemStatus from "./pages/CartItemStatus";
import ProductsForSaleCategories from "./pages/ProductsForSaleCategories";
import Promotions from "./pages/Promotions";
import ClientPromotions from "./pages/client/ClientPromotions";
import Login from "./pages/Login";
import OrderSummary from "./pages/OrderSummary";
import ClientRestorePassword from "./pages/client/ClientRestorePassword";
import TermsAndConditions from "./pages/TermsAndConditions";
import ChooseMembershipPage from "./pages/ChooseMembershipPage";
import ProtectedRoute from "./components/ProtectedRoute";
import ClientMemberships from "./pages/client/ClientMemberships";
import ClientRecurringProfile from "./pages/client/ClientRecurringProfile";
import Memberships from "./pages/Memberships";
import ClientWaitingList from "./pages/client/ClientWaitingList";
import WaitingListDetails from "./pages/WaitingListDetails";
import WaitingListJoined from "./pages/WaitingListJoined";

const publicRoutes = [
  { path: ROUTES.LOCATIONS, element: <Locations /> },
  { path: ROUTES.SERVICES, element: <Services /> },
  { path: ROUTES.SERVICES_HOME, element: <Home back={ROUTES.HOME} /> },
  { path: ROUTES.CLASSES, element: <Classes /> },
  { path: ROUTES.PROVIDERS, element: <Providers /> },
  { path: ROUTES.ADDONS, element: <Addons /> },
  { path: ROUTES.PRODUCTS, element: <Products /> },
  { path: ROUTES.DATETIME, element: <DateTime /> },
  { path: ROUTES.DETAILS, element: <Details /> },
  { path: ROUTES.PRODUCTS_FOR_SALE_CATEGORIES, element: <ProductsForSaleCategories /> },
  { path: ROUTES.PRODUCTS_FOR_SALE, element: <ProductsForSale /> },
  { path: ROUTES.PROMOTIONS, element: <Promotions /> },
  { path: ROUTES.ADDITIONAL_FIELDS, element: <AdditionalFields /> },
  { path: ROUTES.PACKAGES, element: <Packages /> },
  { path: ROUTES.CHECKOUT, element: <Checkout /> },
  { path: ROUTES.ORDER_SUMMARY, element: <OrderSummary /> },
  { path: ROUTES.BOOKING_STATUS, element: <BookingStatus /> },
  { path: ROUTES.BOOKING_CONFIRMED, element: <BookingStatus status={BOOKING_STATUS.CONFIRMED} /> },
  { path: ROUTES.BOOKING_CANCELLED, element: <BookingStatus status={BOOKING_STATUS.CANCELLED} /> },
  { path: ROUTES.BOOKING_PENDING, element: <BookingStatus status={BOOKING_STATUS.PENDING} /> },
  { path: ROUTES.BOOKING_DELAY, element: <BookingStatus status={BOOKING_STATUS.DELAY} /> },
  { path: ROUTES.BOOKING_RESCHEDULED, element: <BookingStatus status={BOOKING_STATUS.RESCHEDULE_SUCCESS} /> },
  { path: ROUTES.CART_ITEM_STATUS, element: <CartItemStatus /> },
  { path: ROUTES.CART_ITEM_CONFIRMED, element: <CartItemStatus status={BOOKING_STATUS.CONFIRMED} /> },
  { path: ROUTES.CART_ITEM_CANCELLED, element: <CartItemStatus status={BOOKING_STATUS.CANCELLED} /> },
  { path: ROUTES.CART_ITEM_PENDING, element: <CartItemStatus status={BOOKING_STATUS.PENDING} /> },
  { path: ROUTES.CART_ITEM_DELAY, element: <CartItemStatus status={BOOKING_STATUS.DELAY} /> },
  { path: ROUTES.CLIENT_REGISTER, element: <ClientRegister /> },
  { path: ROUTES.PERSONAL_DATA_REPORT, element: <PersonalDataReport /> },
  { path: ROUTES.INVOICE_PAY, element: <InvoicePay /> },
  { path: ROUTES.INVOICE_PAY_LATER, element: <InvoicePay /> },
  { path: ROUTES.INVOICE_DETAILS, element: <InvoiceDetails /> },
  { path: ROUTES.CANCEL_BOOKING, element: <CancelBooking /> },
  { path: ROUTES.RESCHEDULE_BOOKING, element: <RescheduleBooking /> },
  { path: ROUTES.LOGIN, element: <Login /> },
  { path: ROUTES.CLIENT_RESTORE_PASSWORD, element: <ClientRestorePassword /> },
  { path: ROUTES.TERMS_AND_CONDITIONS, element: <TermsAndConditions /> },
  { path: ROUTES.MEMBERSHIPS, element: <Memberships /> },
  { path: ROUTES.MEMBERSHIPS_CHOOSE, element: <ChooseMembershipPage /> },
  { path: ROUTES.WAITING_LIST_DETAILS, element: <WaitingListDetails /> },
  { path: ROUTES.WAITING_LIST_JOINED, element: <WaitingListJoined /> },
];

const protectedRoutes = [
  { path: ROUTES.CLIENT_PROFILE, element: <ProtectedRoute><ClientProfile /></ProtectedRoute> },
  { path: ROUTES.CLIENT_EDIT, element: <ProtectedRoute><ClientProfileEdit /></ProtectedRoute> },
  { path: ROUTES.CLIENT_PASSWORD_CHANGE, element: <ProtectedRoute><ClientPasswordChange /></ProtectedRoute> },
  { path: ROUTES.CLIENT_BOOKINGS, element: <ProtectedRoute><ClientBookings /></ProtectedRoute> },
  { path: ROUTES.CLIENT_PACKAGES, element: <ProtectedRoute><ClientPackages /></ProtectedRoute> },
  { path: ROUTES.CLIENT_INVOICES, element: <ProtectedRoute><ClientInvoices /></ProtectedRoute> },
  { path: ROUTES.CLIENT_PAYMENTS, element: <ProtectedRoute><ClientPayments /></ProtectedRoute> },
  { path: ROUTES.CLIENT_PAYMENT_METHODS, element: <ProtectedRoute><ClientPaymentMethods /></ProtectedRoute> },
  { path: ROUTES.CLIENT_RECURRING_PROFILES, element: <ProtectedRoute><ClientRecurringProfiles /></ProtectedRoute> },
  { path: ROUTES.CLIENT_PROMOTIONS, element: <ProtectedRoute><ClientPromotions /></ProtectedRoute> },
  {
    path: ROUTES.CLIENT_MEMBERSHIPS,
    element: (
      <ProtectedRoute>
        <ClientMemberships />
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.CLIENT_RECURRING_PROFILE,
    element: (
      <ProtectedRoute>
        <ClientRecurringProfile />
      </ProtectedRoute>
    ),
  },
  {
    path: ROUTES.CLIENT_WAITING_LIST,
    element: (
      <ProtectedRoute>
        <ClientWaitingList />
      </ProtectedRoute>
    ),
  },
];

const routes = [...protectedRoutes, ...publicRoutes];

export default routes;
